/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Box from "components/generals/Box";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import DefaultCell from "components/generals/DefaultCell";
import useHttp from "hooks/http.hook";
import { useResetPageOnFilter } from "hooks/tableReset.hook";
import {
  AccountingMovementInterface,
  BillingEncounterInterface,
  HeaderEnum,
  PatientInterface,
  PersonInterface,
  QueryResultInterface,
} from "@malmur-technologies/medicalsystem-shared";
import { CoreAPI_URLs } from "constants/http.const";
import { formatPrice } from "utils/number.utils";
import { Tooltip } from "@mui/material";
import { getFromISODDMMYYYYDateFormat } from "utils/date.utils";
import { Row } from "react-table";
import useSort from "hooks/useSort.hook";
/**
 * Properties for the AccountingMovementsTable component.
 * 
 * @interface AccountingMovementsTableProps
 * @property {string} accountingId - Patient accounting ID to fetch movements for
 * @property {PatientInterface} patient - Patient information for context
 */
interface AccountingMovementsTableProps {
  accountingId: string;
  patient: PatientInterface;
}

/**
 * Accounting movements table component for displaying patient financial transactions.
 * 
 * Features:
 * - Displays all accounting movements (payments and billing encounters)
 * - Server-side pagination with configurable page size
 * - Search/filter functionality
 * - Formatted date display (DD/MM/YYYY)
 * - Type detection (payment vs billing encounter)
 * - Price formatting for amounts
 * - Tooltip support for additional details
 * - Automatic page reset on filter change
 * - Safe array handling
 * 
 * Movement Types:
 * - Payments: Patient payments, insurance payments, refunds
 * - Billing Encounters: Service charges, procedure costs
 * 
 * Table Columns:
 * - Date (formatted)
 * - Type (Payment or Billing Encounter)
 * - Description/details
 * - Amount (formatted currency)
 * - Additional transaction info
 * 
 * Pagination:
 * - Default page size: 10 records
 * - Tracks total pages and rows
 * - Resets to page 1 on filter change
 * 
 * @param {AccountingMovementsTableProps} props - Component properties
 * @param {string} props.accountingId - Accounting ID to load movements for
 * @param {PatientInterface} props.patient - Patient information
 * 
 * @returns {JSX.Element} Accounting movements table with pagination and filtering
 */
const AccountingMovementsTable: React.FC<AccountingMovementsTableProps> = ({
  accountingId,
  patient,
}) => {
  const { t } = useTranslation();
  const [movements, setLocalMovements] = useState<AccountingMovementInterface[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageTotal, setPageTotal] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowsTotal, setRowsTotal] = useState<number>(0);
  const [filterModel, setFilterModel] = useState<string>("");
  const [billingEncountersByPayment, setBillingEncountersByPayment] = useState<Map<string, BillingEncounterInterface[]>>(new Map());
  const [isLoading, _error, sendRequest] = useHttp();
  const { sortBy, sortOrder, handleSortChange, buildSortParam } = useSort();
  useResetPageOnFilter(filterModel, page, setPage);

  const loadMovements = useCallback(() => {
    if (accountingId) {
      const searchParam = filterModel ? `search=${encodeURIComponent(filterModel)}&` : "";
      const endpoint = `${CoreAPI_URLs.ACCOUNTING_MOVEMENT_GENERAL_OPERATIONS}/all?${searchParam}${buildSortParam()}page=${page}&pageSize=${pageSize}`;

      sendRequest<QueryResultInterface<AccountingMovementInterface>, void>(
        endpoint,
        (result) => {
          const movementsArray = Array.isArray(result) ? result : result.results || [];
          const total = Array.isArray(result) ? result.length : result.total || 0;

          setLocalMovements(movementsArray);
          setRowsTotal(total);
          setPageTotal(Math.ceil(total / pageSize));
        },
        "GET",
        undefined,
        [{ header: HeaderEnum.PATIENT_ACCOUNT_NUMBER, value: accountingId }]
      );
    }
  }, [accountingId, page, pageSize, filterModel, sendRequest, buildSortParam]);

  const loadBillingEncounters = useCallback((movementsList: AccountingMovementInterface[]) => {
    const paymentIds = movementsList
      .filter(movement => movement.payment !== null)
      .map(movement => movement.payment!.id)
      .filter((id, index, self) => self.indexOf(id) === index);

    if (paymentIds.length === 0) {
      setBillingEncountersByPayment(new Map());
      return;
    }

    const endpoint = CoreAPI_URLs.BILLING_ENCOUNTER_BY_PAYMENT_IDS;

    sendRequest<BillingEncounterInterface[], void>(
      endpoint,
      (encounters) => {
        const groupedByPayment = new Map<string, BillingEncounterInterface[]>();
        encounters.forEach((encounter) => {
          const paymentRegistered = (encounter as any).paymentRegistered;
          if (paymentRegistered) {
            const existing = groupedByPayment.get(paymentRegistered) || [];
            existing.push(encounter);
            groupedByPayment.set(paymentRegistered, existing);
          }
        });

        setBillingEncountersByPayment(groupedByPayment);
      },
      "POST",
      JSON.stringify({ paymentIds })
    );
  }, [sendRequest]);

  useEffect(() => {
    loadMovements();
  }, [loadMovements]);

  useEffect(() => {
    if (movements.length > 0) {
      loadBillingEncounters(movements);
    }
  }, [movements, loadBillingEncounters]);

  const getUITable = (list: AccountingMovementInterface[]) => ({
    columns: [
      {
        Header: t("accountingMovements.date"),
        accessor: "date",
        sortIndicatorType: "numeric",
        Cell: ({ value }: any) => {
          const formatted = getFromISODDMMYYYYDateFormat(new Date(value));
          return <DefaultCell value={formatted} />;
        },
      },
      {
        Header: t("accountingMovements.type"),
        accessor: "type",
        disableSortBy: true,
        sortType: (rowA: Row<AccountingMovementInterface>, rowB: Row<AccountingMovementInterface>) => {
          const getTypeText = (movement: AccountingMovementInterface) => {
            if (movement.payment !== null) {
              return t("accountingMovements.payment");
            } else if (movement.billingEncounter !== null) {
              return t("accountingMovements.billingEncounter");
            }
            return "";
          };

          const textA = getTypeText(rowA.original);
          const textB = getTypeText(rowB.original);

          return textA.localeCompare(textB);
        },
        Cell: ({ row }: any) => {
          const movement = row.original;
          let value = "";
          let tooltipTitle = "";
          if (movement.payment !== null) {
            value = t("accountingMovements.payment");
            tooltipTitle = t("accountingMovements.paymentDescription");
          } else if (movement.billingEncounter !== null) {
            value = t("accountingMovements.billingEncounter");
            tooltipTitle = t("accountingMovements.billingDescription");
          }
          return (
            <Tooltip title={tooltipTitle} arrow>
              <span><DefaultCell value={value} /></span>
            </Tooltip>
          );
        },
      },
      {
        Header: t("accountingMovements.concept"),
        accessor: (row: AccountingMovementInterface) => {
          if (row.payment) {
            return row.payment.id || "";
          } else if (row.billingEncounter) {
            return row.billingEncounter.pricingName ?? "";
          }
          return "";
        },
        disableSortBy: true,
        Cell: ({ value, row }: any) => {
          const movement = row.original as AccountingMovementInterface;

          if (movement.payment) {
            const paymentId = movement.payment.id;
            const relatedEncounters = billingEncountersByPayment.get(paymentId) || [];

            if (relatedEncounters.length > 0) {
              if (relatedEncounters.length > 3) {
                return (
                  <DefaultCell
                    value={t("accountingMovements.servicesCount", { count: relatedEncounters.length })}
                  />
                );
              }

              const concepts = relatedEncounters
                .map(enc => enc.pricingName)
                .filter(name => name)
                .join(", ");

              return (
                <DefaultCell
                  value={t("accountingMovements.paymentFor", { concepts })}
                />
              );
            } else {
              return <DefaultCell value={t("accountingMovements.payment")} />;
            }
          } else if (movement.billingEncounter) {
            return <DefaultCell value={movement.billingEncounter.pricingName ?? ""} />;
          }

          return <DefaultCell value="" />;
        },
      },
      {
        Header: t("accountingMovements.amount"),
        accessor: (row: AccountingMovementInterface) => {
          if (row.payment) {
            return row.payment.posted ?? 0;
          } else if (row.billingEncounter) {
            return row.billingEncounter.price ?? 0;
          }
          return 0;
        },
        disableSortBy: true,
        Cell: ({ value, row }: any) => {
          const isPayment = Boolean(row.original.payment);
          const sign = isPayment ? "- " : "+ ";
          return <DefaultCell value={sign + formatPrice(value)} />;
        },
      },
      {
        Header: t("accountingMovements.balance"),
        accessor: "amountDue",
        sortIndicatorType: "numeric",
        Cell: ({ value }: any) => {
          return (
            <Tooltip title={t("accountingMovements.amountDue")}>
              <span><DefaultCell value={"$ " + value} /></span>
            </Tooltip>
          );
        },
      },
    ],
    rows: list,
  });

  const tableData = useMemo(() => getUITable(movements), [movements, t, billingEncountersByPayment]);

  return (
    <Box p={3}>
      <DataTable
        table={tableData}
        canSearch={false}
        entriesPerPage={{ defaultValue: pageSize }}
        setPageState={setPage}
        setPageSizeState={setPageSize}
        apiPagination={true}
        apiPageTotal={pageTotal}
        apiRowsTotal={rowsTotal}
        apiPageCurrentIndex={page}
        setModelFilter={setFilterModel}
        isLoading={isLoading}
        onSortChange={handleSortChange}
        currentSortBy={sortBy}
        currentSortOrder={sortOrder}
      />
    </Box>
  );
};

export default AccountingMovementsTable;